import values from 'lodash-es/values'
import { fromSeed, BIP32Interface as BIP32 } from 'bip32'
import { payments, ECPairInterface as ECPair } from 'bitcoinjs-lib'
import { fromWIF, fromPrivateKey } from 'bitcoinjs-lib/src/ecpair'

import { ILogger, RPCNode, OneChainError, RPCError, NetworkError, IKeypair } from '@one-chain/core'
import { mnemonicToSeedSync, getNetworkByChainId } from './helper'
import { IApiProvider, IFastApiProvider } from './api-providers'

export interface IAddress {
  index: number
  address: string
  txCount: number
  balance: number
  ecpair: ECPair
}

export interface IAddressJSON {
  index: number
  address: string
  wif: string
  publicKey: string
  privateKey: string
  txCount: number
  balance: number
}

/**
 * HDWallet
 *
 * @export
 * @class HDWallet
 */
export class HDWallet {
  static ADDRESS_ALL = 'all'
  static ADDRESS_CHANGE = 'change'
  static ADDRESS_RECEIVING = 'receiving'

  protected _logger: ILogger
  protected _rpcnode: RPCNode
  protected _root: BIP32
  protected _apiProvider: IApiProvider
  // 助记词
  protected _mnemonic: string
  // 收款地址
  protected _receiving: IAddress[] = []
  // 找零地址
  protected _change: IAddress[] = []

  // bip44 标准 gap limit
  protected _gapLimit = 20

  // 单次批量请求的数据量
  // 每次批量请求 >20 个地址，虽然有时候只用到前面一部分地址，但这样是网络请求最少的
  protected _batchSize = 100

  constructor (mnemonic: string, rpcnode: RPCNode, apiProvider: IApiProvider, logger: ILogger) {
    this._logger = logger
    this._rpcnode = rpcnode
    this._apiProvider = apiProvider
    this._mnemonic = mnemonic

    // WARNING 不要移除此处的 !PROD ，它们能确保生产环境没有日志代码
    !PROD && this._logger.debug(`BTC - Init HDWallet from ${mnemonic}`)

    const seedStr = mnemonicToSeedSync(mnemonic)
    !PROD && this._logger.trace('BTC - HDWallet seed: ', seedStr)

    const seed = Buffer.from(seedStr, 'hex')
    this._root = fromSeed(seed, getNetworkByChainId(this._rpcnode.chainId))
    !PROD && this._logger.trace('BTC - HDWallet root: ', this._root.toBase58())
  }

  /**
   * 从助记词创建一个 HDWallet
   *
   * @static
   * @param {string} mnemonic
   * @param {RPCNode} rpcnode
   * @param {IApiProvider} apiProvider
   * @param {boolean} [needSync=true]
   * @param {ILogger} logger
   * @returns
   * @memberof HDWallet
   */
  static async fromMnemonic (mnemonic: string, rpcnode: RPCNode, apiProvider: IApiProvider, needSync = true, logger: ILogger) {
    const wallet = new HDWallet(mnemonic, rpcnode, apiProvider, logger)
    if (needSync) {
      await wallet.sync()
    }
    else {
      wallet.init()
    }
    return wallet
  }

  /**
   * 从 JSON 数据创建一个 HDWallet
   *
   * @static
   * @param {*} data
   * @param {RPCNode} rpcnode
   * @param {IApiProvider} apiProvider
   * @param {ILogger} logger
   * @returns
   * @memberof HDWallet
   */
  static async fromJSON (data: any, rpcnode: RPCNode, apiProvider: IApiProvider, logger: ILogger) {
    const wallet = new HDWallet(data.mnemonic, rpcnode, apiProvider, logger)
    wallet._recover(data.receiving, data.change)
    return wallet
  }

  /**
   * HDWallet 转为 JSON 对象
   *
   * @returns
   * @memberof HDWallet
   */
  toJSON () {
    function _serialize (address: IAddress): IAddressJSON {
      return {
        index: address.index,
        address: address.address,
        wif: address.ecpair.toWIF(),
        publicKey: address.ecpair.publicKey.toString('hex'),
        privateKey: address.ecpair.privateKey.toString('hex'),
        txCount: address.txCount,
        balance: address.balance,
      }
    }

    return {
      mnemonic: this._mnemonic,
      receiving: this._receiving.map(_serialize),
      change: this._change.map(_serialize),
    }
  }

  /**
   * 获取所有 keypair
   *
   * @param {*} [type=HDWallet.ADDRESS_ALL]
   * @returns
   * @memberof HDWallet
   */
  getKeypairs (type = HDWallet.ADDRESS_ALL): IKeypair[] {
    let ret = []

    function getKeypair (address: IAddress): IKeypair {
      return {
        address: address.address,
        privateKey: address.ecpair.toWIF(),
      }
    }

    if (type === HDWallet.ADDRESS_RECEIVING) {
      ret = this._receiving.map(getKeypair)
    }
    else if (type === HDWallet.ADDRESS_CHANGE) {
      ret = this._change.map(getKeypair)
    }
    else {
      ret = ret.concat(this._receiving.map(getKeypair))
      ret = ret.concat(this._change.map(getKeypair))
    }

    return ret
  }

  /**
   *
   * 自动更新 HDWallet 状态
   *
   * 主要是刷新可用收款地址和找零地址
   *
   * @param {boolean} [force=false] 是否强制从头同步
   * @returns
   * @memberof HDWallet
   */
  async sync (force = false) {
    this._logger.debug('BTC - HDWallet start sync... ')

    return Promise.all([
      this._discoverReceiving(force),
      this._discoverChange(force),
    ])
  }

  /**
   * 静态初始化 HDWallet
   *
   * @memberof HDWallet
   */
  init () {
    const coinType = this._rpcnode.chainId === 'mainnet' ? '0' : '1'
    this._receiving = this._derive(coinType, '0')
    this._change = this._derive(coinType, '1')
  }

  /**
   * 主动更新 HDWallet 状态
   *
   * 主要用于从缓存重建 HDWallet 的情况
   *
   * @param {IAddressJSON[]} receiving
   * @param {IAddressJSON[]} change
   * @memberof HDWallet
   */
  protected _recover (receiving: IAddressJSON[], change: IAddressJSON[]) {
    function _deserialize (val: any): IAddress {
      const ecpair: ECPair = fromWIF(val.wif)
      return {
        index: val.index,
        address: val.address,
        txCount: val.txCount,
        balance: val.balance,
        ecpair,
      }
    }

    // 只更新输入的地址
    receiving.map(_deserialize).forEach(item => {
      this._receiving[item.index] = item
    })
    change.map(_deserialize).forEach(item => {
      this._change[item.index] = item
    })
  }

  /**
   * 推导 20 个地址
   *
   * 对于不需要同步的情况，可以使用此接口默认推导前面 20 个地址。
   *
   * @protected
   * @param {string} coinType
   * @param {string} isChange
   * @returns
   * @memberof HDWallet
   */
  protected _derive (coinType: string, isChange: string) {
    const path = this._root.derivePath(`m/44'/${coinType}'/0'/${isChange}`)

    const addresses = []
    for (let i = 0; i < 20; i++) {
      const node = path.derive(i)
      const ecpair: ECPair = fromPrivateKey(node.privateKey)
      const p2pkh = payments.p2pkh({ pubkey: ecpair.publicKey, network: getNetworkByChainId(this._rpcnode.chainId) })
      addresses.push({
        index: i,
        address: p2pkh.address,
        txCount: 0,
        balance: '0',
        ecpair: ecpair,
      })
    }
    return addresses
  }

  /**
   * 发现可用收款地址
   *
   * @protected
   * @param {boolean} [force=false]
   * @memberof HDWallet
   */
  protected async _discoverReceiving (force = false) {
    const coinType = this._rpcnode.chainId === 'mainnet' ? '0' : '1'
    const { path, startAt, gap } = this._calcFetchStart(coinType, '0', this._receiving, force)
    let fetchedAddress
    if (this._apiProvider.supportBatch) {
      fetchedAddress = await this._batchFetchAddresses(path, startAt, gap, [])
    }
    else {
      fetchedAddress = await this._fetchAddresses(path, startAt, gap, [])
    }
    this._receiving = this._receiving.concat(fetchedAddress)
  }

  /**
   * 发现可用找零地址
   *
   * @protected
   * @param {boolean} [force=false]
   * @memberof HDWallet
   */
  protected async _discoverChange (force = false) {
    const coinType = this._rpcnode.chainId === 'mainnet' ? '0' : '1'
    const { path, startAt, gap } = this._calcFetchStart(coinType, '1', this._change, force)
    let fetchedAddress
    if (this._apiProvider.supportBatch) {
      fetchedAddress = await this._batchFetchAddresses(path, startAt, gap, [])
    }
    else {
      fetchedAddress = await this._fetchAddresses(path, startAt, gap, [])
    }
    this._change = this._change.concat(fetchedAddress)
  }

  /**
   * 计算 fetch 起始参数
   *
   * @protected
   * @param {string} coinType
   * @param {string} isChange
   * @param {IAddress[]} addresses
   * @param {boolean} [force=false]
   * @returns
   * @memberof HDWallet
   */
  protected _calcFetchStart (coinType: string, isChange: string, addresses: IAddress[], force = false) {
    let startAt = 0
    let gap = 0

    // 如果没有强制从头 fetch 且已存在地址不为空，那么就找出起始 index 和最后的 gap
    if (!force && addresses.length > 0) {
      let lastAddress
      for (const address of this._receiving) {
        // 关于 gap 的计算详见 https://github.com/bitcoin/bips/blob/master/bip-0044.mediawiki#account-discovery
        gap = address.txCount > 0 ? 0 : gap + 1
        lastAddress = address
      }
      startAt = lastAddress.index + 1
    }

    const path = this._root.derivePath(`m/44'/${coinType}'/0'/${isChange}`)

    this._logger.debug(`BTC - HDWallet fetch start at: m/44'/${coinType}'/0'/${isChange}/${startAt} gap: ${gap}`)
    return { path, startAt, gap }
  }

  /**
   * 根据 apiProvider 的频率限制 fetch 可用地址
   *
   * @protected
   * @param {BIP32} path
   * @param {number} index
   * @param {number} gap
   * @param {IAddress[]} [fetchedAddress=[]]
   * @returns {Promise<IAddress[]>}
   * @memberof HDWallet
   */
  protected async _fetchAddresses (path: BIP32, index: number, gap: number, fetchedAddress: IAddress[] = []): Promise<IAddress[]> {
    const addresses: IAddress[] = []
    for (let i = 0; i < this._apiProvider.rateLimit; i++) {
      const node = path.derive(index++)
      const ecpair: ECPair = fromPrivateKey(node.privateKey)
      const p2pkh = payments.p2pkh({ pubkey: ecpair.publicKey, network: getNetworkByChainId(this._rpcnode.chainId) })
      addresses.push({
        index: index + i,
        address: p2pkh.address,
        txCount: 0,
        balance: 0,
        ecpair: ecpair,
      })

      this._logger.debug('BTC - HDWallet._fetchAddresses try to fetch: ', p2pkh.address)
    }

    return addresses

    // TODO 接入实际的接口服务
    // const startTimestamp = Date.now()
    // try {
    //   const results = await Promise.all(addresses.map(address => this._apiProvider.getAddress(address.address)))
    //   results.forEach((v, i) => {
    //     // 使用 api 返回数据更新当前 address 信息，因为 Promise.all 返回值的顺序和输入是一致的，所以可以直接使用索引
    //     addresses[i].balance = v.balance
    //     addresses[i].txCount = v.txCount
    //     this._logger.debug(`BTC - HDWallet fetched ${v.address}, txCount: ${v.txCount} balance: ${v.balance}`)

    //     // 关于 gap 的计算详见 https://github.com/bitcoin/bips/blob/master/bip-0044.mediawiki#account-discovery
    //     if (v.txCount > 0) {
    //       gap = 0
    //       this._logger.debug(`BTC - HDWallet gap count reset at: ${v.address}`)
    //     } else {
    //       gap += 1
    //     }
    //   })
    // } catch (err) {
    //   this._logger.error(err)
    //   throw OneChainError.fromCode(107)
    // }

    // fetchedAddress = fetchedAddress.concat(addresses)
    // if (gap >= this._gapLimit) {
    //   return fetchedAddress
    // } else {
    //   // 每隔 1100ms 执行一次，这里的 1100ms 包含了本函数内的执行时间
    //   // 之所以不是 1000ms 是为了避免误差导致请求频率高于 apiProvider 的限制
    //   const usedTime = Date.now() - startTimestamp
    //   return new Promise((resolve, reject) => {
    //     const timer = setTimeout(() => {
    //       this._fetchAddresses(path, index, gap, fetchedAddress).then(resolve, reject)
    //       clearTimeout(timer)
    //     }, 1500 - usedTime)
    //   })
    // }
  }

  /**
   * 根据 apiProvider 的频率限制批量 fetch 可用地址
   *
   * @protected
   * @param {BIP32} path
   * @param {number} index
   * @param {number} gap
   * @param {IAddress[]} [fetchedAddress=[]]
   * @returns {Promise<IAddress[]>}
   * @memberof HDWallet
   */
  protected async _batchFetchAddresses (path: BIP32, index: number, gap: number, fetchedAddress: IAddress[] = []): Promise<IAddress[]> {
    const addressesMap: { [key: string]: IAddress } = {}
    for (let i = 0; i < this._batchSize; i++) {
      const node = path.derive(index)
      const ecpair: ECPair = fromPrivateKey(node.privateKey)
      const p2pkh = payments.p2pkh({ pubkey: ecpair.publicKey, network: getNetworkByChainId(this._rpcnode.chainId) })
      addressesMap[p2pkh.address] = {
        index: index,
        address: p2pkh.address,
        txCount: 0,
        balance: 0,
        ecpair: ecpair,
      }
      index++
    }
    // console.log(JSON.stringify(values(addressesMap).map(v => v.address)))

    const startTimestamp = Date.now()
    let stopDerive = false
    try {
      const addresses = values(addressesMap).map(v => v.address)
      const results = await (<IFastApiProvider> this._apiProvider).getAddresses(addresses)

      for (const v of results) {
        addressesMap[v.address].balance = v.balance
        addressesMap[v.address].txCount = v.txCount
        if (v.txCount > 0) {
          this._logger.trace(`BTC - HDWallet._batchFetchAddresses has tx keypair[${path.index}]: [${v.address}] [${addressesMap[v.address].ecpair.toWIF()}]`)
        }

        // 关于 gap 的计算详见 https://github.com/bitcoin/bips/blob/master/bip-0044.mediawiki#account-discovery
        if (v.txCount > 0) {
          gap = 0
          this._logger.trace(`BTC - HDWallet._batchFetchAddresses[${path.index}] gap count reset at: [${v.address} ${v.txCount}]:`, gap)
        }
        else {
          gap += 1
          this._logger.trace(`BTC - HDWallet._batchFetchAddresses[${path.index}] gap count increase at [${v.address} ${v.txCount}]:`, gap)
        }

        // 将地址加入返回数组
        fetchedAddress.push(addressesMap[v.address])

        // gap 达到 20 就停止地址推导
        if (gap >= this._gapLimit) {
          stopDerive = true
          break
        }
      }
    }
    catch (err) {
      this._logger.error(err)

      if (err instanceof RPCError || err instanceof NetworkError) {
        throw err
      }
      else {
        throw OneChainError.fromCode(107)
      }
    }

    if (stopDerive) {
      return fetchedAddress
    }
    else {
      const usedTime = Date.now() - startTimestamp
      return new Promise((resolve, reject) => {
        const timer = setTimeout(() => {
          this._batchFetchAddresses(path, index, gap, fetchedAddress).then(resolve, reject)
          clearTimeout(timer)
        // 最快每隔 500ms 执行一次，如果单次执行超过 500ms 那就不等待了
        }, usedTime > 500 ? 0 : 500)
      })
    }
  }
}
